import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AddCircleBlue, CustomDelete, Loader, NoItemsYet } from 'components';
import { FindLoad, Images, PermissionList, RolePermission, useModal } from 'utils';
import { tutorialVideosActions } from 'store';
import { TutorialVideoPlayer } from './tutorialVideoPlayer';
import { TutorialVideoForm } from './tutorialVideoForm';
import { ACTION_TYPE, ALL_VIDEOS_TAB, buildTabs, DELETE_ACTION_TYPE, formatDuration, TutorialVideoSource } from './constants';

const PLAYER_MODAL_STYLES = {
    styles: {
        wrapperStyle: { padding: '0px', borderRadius: '8px', background: 'black' },
        bodyStyle: { padding: '0px', borderRadius: '8px', background: 'black' },
    },
};

export const TutorialVideos = () => {
    const dispatch = useDispatch();
    const { open } = useModal();
    const tutorialVideos = useSelector((state) => state.tutorialVideos.tutorialVideos);
    const loader = FindLoad(ACTION_TYPE);
    const [activeTab, setActiveTab] = useState(ALL_VIDEOS_TAB);
    const canManage = RolePermission([PermissionList.TUTORIAL_VIDEO_MANAGE?.code]);

    useEffect(() => {
        dispatch(tutorialVideosActions.getTutorialVideos());
    }, []);

    const tabs = useMemo(() => buildTabs(tutorialVideos), [tutorialVideos]);

    const visibleVideos = useMemo(() => {
        if (activeTab === ALL_VIDEOS_TAB) return tutorialVideos;
        return tutorialVideos?.filter((video) => video.category === activeTab);
    }, [tutorialVideos, activeTab]);

    const openPlayer = (video) => open(<TutorialVideoPlayer video={video} />, PLAYER_MODAL_STYLES);

    const openForm = (video) => open(<TutorialVideoForm video={video} />);

    const openDelete = (video) =>
        open(
            <CustomDelete
                info="Delete Video?"
                text="This video and its file will be permanently removed."
                actionType={DELETE_ACTION_TYPE}
                handleDel={() => dispatch(tutorialVideosActions.deleteTutorialVideo(video?.id || video?._id))}
                body={
                    <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                        <img style={{ marginTop: 4 }} src={Images.videos} alt="icon" />
                        <p style={{ fontWeight: 600 }}>{video?.title}</p>
                    </div>
                }
            />
        );

    if (loader?.length) {
        return <Loader />;
    }

    return (
        <div className="tutorial-videos-container">
            <div className="tutorial-videos-header">
                <div className="tutorial-videos-header-row">
                    <div>
                        <h1 className="tutorial-videos-title">Tutorial Videos</h1>
                        <p className="tutorial-videos-subtitle">
                            Watch training and how-to videos to learn how to use TherapyLake.
                        </p>
                    </div>

                    {canManage && (
                        <div className="tutorial-videos-add">
                            <AddCircleBlue
                                type="button"
                                text={'Upload Video'}
                                icon={Images.uploadDockCloud}
                                handleClick={() => openForm(null)}
                            />
                        </div>
                    )}
                </div>

                <div className="tutorial-videos-tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.value}
                            type="button"
                            className={`tutorial-videos-tab ${activeTab === tab.value ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.value)}
                        >
                            {tab.label}
                            <span className="tutorial-videos-tab-count">{tab.count}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="tutorial-videos-body">
                {!visibleVideos?.length ? (
                    <NoItemsYet
                        text={'No tutorial videos yet'}
                        subTitle={'Upload videos that show your team how to work in TherapyLake.'}
                        image={Images.emptyFolder}
                    />
                ) : (
                    <>
                        <p className="tutorial-videos-count">
                            {visibleVideos.length} {visibleVideos.length === 1 ? 'video' : 'videos'} available
                        </p>

                        <div className="tutorial-videos-grid">
                            {visibleVideos.map((video) => (
                                <TutorialVideoCard
                                    key={video.id || video._id}
                                    video={video}
                                    canManage={canManage}
                                    handlePlay={() => openPlayer(video)}
                                    handleEdit={() => openForm(video)}
                                    handleDelete={() => openDelete(video)}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

const TutorialVideoCard = ({ video, canManage, handlePlay, handleEdit, handleDelete }) => {
    const duration = formatDuration(video?.durationSec);
    const isEditable = canManage;

    return (
        <div className="tutorial-video-card">
            <button type="button" className="tutorial-video-card-main" onClick={handlePlay}>
                <div className="tutorial-video-cover">
                    {video?.thumbnail?.url ? (
                        <img src={video.thumbnail.url} alt={video.title} />
                    ) : (
                        <div className="tutorial-video-cover-fallback" />
                    )}

                    <span className="tutorial-video-play" aria-hidden="true" />
                    {duration && <span className="tutorial-video-duration">{duration}</span>}
                </div>

                <div className="tutorial-video-info">
                    <p className="tutorial-video-name">{video?.title}</p>
                    {video?.description && <p className="tutorial-video-description">{video.description}</p>}
                </div>
            </button>

            {isEditable && video?.source !== TutorialVideoSource.PLATFORM && (
                <div className="tutorial-video-actions">
                    <button type="button" title="Edit" onClick={handleEdit}>
                        <img src={Images.edit} alt="edit" />
                    </button>
                    <button type="button" title="Delete" onClick={handleDelete}>
                        <img src={Images.remove} alt="delete" />
                    </button>
                </div>
            )}
        </div>
    );
};
